import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { BRAND } from "@/config/brand";
import { useLocalePath } from "@/lib/locale";

/*
 * Apple.com footer replica: #f5f5f7, 12px type, thin #d2d2d7 dividers,
 * ordered directory columns, discreet legal line.
 */

const FooterColumn = ({ title, links }) => (
  <div className="flex flex-col gap-2.5">
    <span className="text-xs font-semibold text-[#1d1d1f]">{title}</span>
    {links.map((link) => (
      <Link
        key={link.testid}
        to={link.to}
        data-testid={link.testid}
        className="text-xs text-[#424245] hover:underline w-fit"
      >
        {link.label}
      </Link>
    ))}
  </div>
);

export const Footer = () => {
  const { t } = useTranslation();
  const lp = useLocalePath();

  return (
    <footer data-testid="app-footer" className="bg-[#f5f5f7]">
      <div className="max-w-[1024px] mx-auto px-4 lg:px-6 py-8">
        <p className="text-xs text-[#6e6e73] leading-relaxed max-w-2xl pb-5 border-b border-[#d2d2d7]">
          {t("footer.tagline")}
        </p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 py-6">
          <FooterColumn
            title={t("footer.platform")}
            links={[
              { to: lp("/auctions"), label: t("nav.auctions"), testid: "footer-auctions" },
              { to: lp("/for-organisers"), label: t("nav.forOrganisers"), testid: "footer-for-organisers" },
              { to: lp("/for-buyers"), label: t("nav.forBuyers"), testid: "footer-for-buyers" },
              { to: lp("/features"), label: t("nav.features"), testid: "footer-features" },
              { to: lp("/pricing"), label: t("nav.pricing"), testid: "footer-pricing" },
              { to: lp("/how-it-works"), label: t("nav.howItWorks"), testid: "footer-how-it-works" },
              { to: lp("/resources"), label: t("kb.title"), testid: "footer-resources" },
            ]}
          />
          <FooterColumn
            title={t("footer.company")}
            links={[
              { to: lp("/about"), label: t("footer.about"), testid: "footer-about" },
              { to: lp("/contact"), label: t("footer.contact"), testid: "footer-contact" },
            ]}
          />
          <FooterColumn
            title={t("footer.legal")}
            links={[
              { to: lp("/legal/terms"), label: t("footer.terms"), testid: "footer-terms" },
              { to: lp("/legal/privacy"), label: t("footer.privacy"), testid: "footer-privacy" },
              { to: lp("/legal/cookies"), label: t("footer.cookies"), testid: "footer-cookies" },
            ]}
          />
          <div className="flex flex-col gap-2.5">
            {/* Acceso discreto al panel de administración de plataforma (Fase 10) */}
            <Link to="/admin" data-testid="footer-logo-link" aria-label={BRAND.name} className="self-start">
              <img src={BRAND.logo} alt={BRAND.name} className="h-[16px] w-auto" draggable={false} data-testid="footer-logo" />
            </Link>
            <p className="text-xs text-[#424245] leading-relaxed max-w-[200px]">{t("footer.poweredBy", { name: BRAND.poweredBy })}</p>
          </div>
        </div>

        <div className="pt-4 border-t border-[#d2d2d7] flex flex-col sm:flex-row justify-between gap-2">
          <span className="text-xs text-[#6e6e73]">
            © {new Date().getFullYear()} {BRAND.legalName}. {t("footer.rights")}
          </span>
          <span className="text-xs text-[#6e6e73]">{BRAND.domain}</span>
        </div>
      </div>
    </footer>
  );
};
